import { EmailContentView } from "components/EmailContentView";
import { RelativeTimeDisplay } from "components/RelativeTimeDisplay/RelativeTimeDisplay";
import type { EmailDocument } from "hooks/useEmailsRestApi";
import { type FC, type MouseEventHandler, useCallback, useState } from "react";
import ReactModal from "react-modal";
import { modalStyle } from "routes/emails/SearchEmailsPage/components/EmailDetailModal/EmailDetailModalModalStyle";
import { toastError, toastPromise } from "utils/toast-utils";
import { ReactComponent as CheckIcon } from "./check.svg";
import { ReactComponent as ClosedIcon } from "./closed.svg";
import { ReactComponent as CopyIcon } from "./copy.svg";

type EmailDetailModalProps = {
  visibility: boolean;
  selectedEmail: EmailDocument;
  onClosed: MouseEventHandler;
  freeWords: string[];
};
ReactModal.setAppElement("#root");

export const EmailDetailModal: FC<EmailDetailModalProps> = ({
  visibility,
  selectedEmail,
  onClosed,
  freeWords,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const copyEmail = useCallback(async () => {
    if (isCopied) {
      // コピー済みの場合は何もしない
      return;
    }

    // タイトルと本文取得
    const title: string = selectedEmail.subject || "タイトルなし";
    const mainText: string = selectedEmail.content || "本文なし";
    const allText: string = [title, mainText].join("\n");

    // クリップボードにテキストをコピー
    if (allText) {
      window.focus();
      try {
        await toastPromise(navigator.clipboard.writeText(allText), {
          pending: "メール内容をコピー中...",
          success: "メール内容をコピーしました！",
          error: "メール内容のコピーに失敗しました。",
        });
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1000);
      } catch (_err) {
        toastError("メール内容のコピーに失敗しました。");
        return;
      }
    }
  }, [isCopied, selectedEmail.subject, selectedEmail.content]);

  return (
    <ReactModal
      style={modalStyle}
      isOpen={visibility}
      onRequestClose={onClosed}
    >
      <div className="flex flex-col h-full">
        <div className="flex justify-between bg-primary text-white py-2 px-2.5">
          <div className="flex flex-col">
            <h3
              id="mailTitle"
              className="text-sm font-bold font-hiragino-sans-w7"
            >
              {selectedEmail.subject}
            </h3>
            <p className="text-nano mt-1 ml-2.5">
              {`from: ${selectedEmail.from.name}(${selectedEmail.from.address})`}
              date: <RelativeTimeDisplay date={selectedEmail.date} />
            </p>
          </div>
          <div className="flex flex-row items-center">
            <span className="relative w-7 h-7 inline-block">
              <CheckIcon
                className={`absolute top-0 left-0 w-7 h-7 transition-all duration-500 ease-in-out ${
                  isCopied
                    ? "opacity-100 scale-100"
                    : "opacity-0 scale-75 pointer-events-none"
                }`}
              />
              <CopyIcon
                onClick={copyEmail}
                className={`absolute top-0 left-0 w-7 h-7 cursor-pointer rounded hover:bg-zinc-600 active:bg-zinc-700 transition-all duration-500 ease-in-out ${
                  isCopied
                    ? "opacity-0 scale-125 pointer-events-none"
                    : "opacity-100 scale-100"
                }`}
              />
            </span>
            <ClosedIcon
              onClick={onClosed}
              className="ml-2 w-7 h-7 cursor-pointer hover:bg-zinc-600 active:bg-zinc-700 rounded"
            />
          </div>
        </div>
        <div
          id="mailDetail"
          className="text-xs px-2.5 py-5 overflow-y-scroll h-email-detail-content"
        >
          <EmailContentView
            content={selectedEmail.content}
            freeWords={freeWords}
            highlighterType="Modal"
          />
        </div>
      </div>
    </ReactModal>
  );
};
